import { AxiosError } from 'axios';
import { action, flow, makeObservable, observable, ObservableMap } from 'mobx';
import getSubscriptions, {
  getSubscription,
  getUserSubscriptions,
  ISubscription,
  ISubscriptionQuery
} from 'requests/subscriptions';
import { getDateRangeWithLabel } from 'utils';

const { endDate, label, startDate } = getDateRangeWithLabel('Last 7 Days');

export const subscriptionKeyToLabelMap = {
  inserted_at: 'Timestamp',
  customer_name: 'Customer name',
  phone_number: 'Phone number',
  total_number_of_shares: 'Total no of shares',
  average_cost: 'Average cost',
  offer_name: 'Offer name',
  allocation_status: 'Allocation status',
  total_amount_subscribed: 'Total amount subscribed',
  start_date: 'Start date',
  close_date: 'Close date'
} as const;

export const subscriptionOrderKeyToLabelMap = {
  inserted_at: 'Inserted at',
  offer_name: 'Offer name',
  type: 'Subscription type',
  total_amount: 'Amount subscribed',
  status: 'Subscription status',
  allocation_status: 'Allocation status',
  chn: 'CHN',
  cscs_no: 'CSCS number',
  broker_id: 'Broker ID',
  broker_name: 'Broker name'
} as const;

export class SubscriptionStoreImpl {
  subscriptions: ISubscription[] = [];

  userSubscriptions: ISubscription[] = [];

  selected: ISubscription | undefined = undefined;

  isLoading = {
    fetchSubscriptions: false,
    fetchSubscription: false,
    fetchUserSubscriptions: false
  };

  errors = {
    fetchSubscriptions: '',
    fetchSubscription: '',
    fetchUserSubscriptions: ''
  };

  signals = new ObservableMap<keyof typeof this.isLoading, AbortController>();

  search = '';

  meta: Partial<IPaginatedMeta> = {
    currentPage: 0,
    itemCount: 0,
    totalPages: 1,
    totalItems: 0
  };

  tableConfig: ISetDateRangeCBArgs & ITableConfig = {
    startDate,
    endDate,
    buttonLabel: label,
    reset: false
  };

  constructor() {
    makeObservable(this, {
      subscriptions: observable,
      userSubscriptions: observable,
      selected: observable,
      isLoading: observable,
      errors: observable,
      meta: observable,
      search: observable,
      tableConfig: observable,
      fetchSubscriptions: flow.bound,
      fetchSubscription: flow.bound,
      fetchUserSubscriptions: flow.bound,
      clearSelected: action.bound,
      clearUserSubscriptions: action.bound,
      setRange: action.bound,
      setSearch: action.bound,
      setReqError: action.bound,
      toggleReset: action.bound
    });
  }

  setRange(config: ISetDateRangeCBArgs) {
    this.tableConfig = { ...config, reset: true };
    this.subscriptions = [];
  }

  toggleReset(reset: boolean) {
    this.tableConfig.reset = reset;
  }

  setSearch(search: string) {
    this.search = search;
  }

  setReqError(error: AxiosError, state: keyof typeof this.errors) {
    if (String(error?.message).toLowerCase() === 'canceled') return;

    if (error.response?.status !== 401) {
      this.errors[state] = (error.response?.data as { message?: string })?.message || error.message;
      setTimeout(() => {
        this.errors[state] = '';
      }, 10000);
    }
  }

  *fetchSubscriptions(params: ISubscriptionQuery, controller = new AbortController()) {
    const request = 'fetchSubscriptions';
    this.signals.get(request)?.abort();
    this.signals.set(request, controller);
    this.isLoading[request] = true;
    this.errors[request] = '';

    try {
      const {
        data: { items, meta }
      } = yield getSubscriptions(params, controller.signal);
      if (this.signals.get(request) !== controller) return;

      this.subscriptions = items;
      this.meta = {
        currentPage: meta?.currentPage ?? params.page ?? 0,
        totalPages: meta?.totalPages || 1,
        totalItems: meta?.totalItems ?? items.length,
        itemCount: meta?.itemCount ?? items.length,
        itemsPerPage: meta?.itemsPerPage || params.limit
      };
    } catch (error) {
      if (this.signals.get(request) !== controller) return;
      this.setReqError(error as AxiosError, request);
      this.subscriptions = [];
    } finally {
      if (this.signals.get(request) === controller) {
        this.isLoading[request] = false;
        this.signals.delete(request);
      }
    }
  }

  *fetchSubscription(id: string, controller = new AbortController()) {
    const request = 'fetchSubscription';
    this.signals.get(request)?.abort();
    this.signals.set(request, controller);
    this.selected = undefined;
    this.isLoading[request] = true;
    this.errors[request] = '';

    try {
      const { data } = yield getSubscription(id, controller.signal);
      if (this.signals.get(request) !== controller) return;
      this.selected = { ...data, orders: data.orders || [] };
    } catch (error) {
      if (this.signals.get(request) !== controller) return;
      this.setReqError(error as AxiosError, request);
      this.selected = undefined;
    } finally {
      if (this.signals.get(request) === controller) {
        this.isLoading[request] = false;
        this.signals.delete(request);
      }
    }
  }

  *fetchUserSubscriptions(userId: string, controller = new AbortController()) {
    const request = 'fetchUserSubscriptions';
    this.signals.get(request)?.abort();
    this.signals.set(request, controller);
    this.isLoading[request] = true;
    this.errors[request] = '';
    this.userSubscriptions = [];

    try {
      const { data } = yield getUserSubscriptions(userId, controller.signal);
      if (this.signals.get(request) !== controller) return;
      this.userSubscriptions = data;
    } catch (error) {
      if (this.signals.get(request) !== controller) return;
      const requestError = error as AxiosError;
      if (requestError.response?.status === 404) {
        this.errors[request] = 'User not found.';
      } else {
        this.setReqError(requestError, request);
      }
    } finally {
      if (this.signals.get(request) === controller) {
        this.isLoading[request] = false;
        this.signals.delete(request);
      }
    }
  }

  clearSelected() {
    const request = 'fetchSubscription';
    this.signals.get(request)?.abort();
    this.signals.delete(request);
    this.isLoading[request] = false;
    this.errors[request] = '';
    this.selected = undefined;
  }

  clearUserSubscriptions() {
    const request = 'fetchUserSubscriptions';
    this.signals.get(request)?.abort();
    this.signals.delete(request);
    this.isLoading[request] = false;
    this.errors[request] = '';
    this.userSubscriptions = [];
  }
}

const SubscriptionStore = new SubscriptionStoreImpl();
export default SubscriptionStore;
